import { useState } from "react";
import { Button } from "../../ui";
import api from "../../../utils/api";

interface ReportParams {
  type: string;
  deviceIds: string[];
  startDate: string;
  endDate: string;
  format: "excel" | "pdf";
}

interface ReportActionBlockProps {
  params: ReportParams;
}

export default function ReportActionBlock({ params }: ReportActionBlockProps) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateAndDownload = async () => {
    setIsDownloading(true);
    setError(null);

    try {
      console.log("[ReportActionBlock] Creating report job...", { type: params.type, format: params.format });

      // Step 1: Create report job via background endpoint
      const createResponse = await api.post("/reports/background", {
        typeReport: params.type,
        typeDocument: params.format,
        startDate: params.startDate,
        endDate: params.endDate,
        deviceIds: params.deviceIds,
      });

      const reportId = createResponse.data?.data?.reportId;
      if (!reportId) {
        throw new Error("El servidor no retornó un ID de reporte válido.");
      }

      console.log("[ReportActionBlock] Report job created:", { reportId });

      // Step 2: Poll for completion (max 5 minutes)
      const maxWaitTime = 5 * 60 * 1000; // 5 minutes
      const pollInterval = 2000; // 2 seconds
      const startTime = Date.now();

      let status = "pending";
      let fileUrl: string | null = null;

      while (Date.now() - startTime < maxWaitTime && status === "pending") {
        console.log("[ReportActionBlock] Polling report status...", { reportId });
        const statusResponse = await api.get("/reports/status", {
          params: { reportId },
        });

        console.log("[ReportActionBlock] Status response:", {
          fullResponse: statusResponse.data,
          status: statusResponse.data?.data?.status,
          fileUrl: statusResponse.data?.data?.fileUrl,
        });

        status = statusResponse.data?.data?.status;
        fileUrl = statusResponse.data?.data?.fileUrl;

        console.log("[ReportActionBlock] After parsing:", { status, fileUrl });

        if (status === "completed" && fileUrl) {
          console.log("[ReportActionBlock] Report completed, downloading...", { reportId, fileUrl });
          downloadFromUrl(fileUrl);
          return;
        }

        if (status === "failed" || status === "error") {
          throw new Error("Error generando el reporte. Intenta nuevamente.");
        }

        // Wait before next poll
        await new Promise((resolve) => setTimeout(resolve, pollInterval));
      }

      if (status === "PENDING") {
        throw new Error("El reporte tardó demasiado en generarse. Intenta nuevamente.");
      }

      throw new Error("Estado de reporte desconocido.");
    } catch (err) {
      console.error("[ReportActionBlock] Error:", err);
      setError(err instanceof Error ? err.message : "Error al descargar el reporte");
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadFromUrl = (url: string) => {
    console.log("[ReportActionBlock] Opening download URL in new tab:", url);
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => document.body.removeChild(a), 100);
  };

  if (error) {
    return (
      <div className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">
        ❌ Error: {error}
      </div>
    );
  }

  return (
    <div className="mt-3">
      <Button
        onClick={generateAndDownload}
        disabled={isDownloading}
        className="flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
        icon={<i className={isDownloading ? "ri-loader-4-line animate-spin" : "ri-download-2-line"} />}
      >
        {isDownloading ? "Cargando..." : "Descargar Reporte"}
      </Button>
    </div>
  );
}
